// API service for model loading from backend API (Google Drive integration)

interface RelatedFile {
  name: string;
  id: string;
  url: string;
  type: string;
}

interface ModelInfo {
  name: string;
  objId: string;
  objUrl: string;
  relatedFiles: RelatedFile[];
}

interface ModelListResponse {
  models: ModelInfo[];
  count: number;
  debug?: {
    requestedFolderId?: string;
    usedFolderId: string;
    defaultFolderId?: string;
    totalFiles: number;
  };
}

export type { RelatedFile, ModelInfo, ModelListResponse };

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3001/api';
const DEBUG = import.meta.env.DEV;

const CACHE_TTL_MS = 60_000; // 1 minute
const folderCache = new Map<string, { models: ModelInfo[]; expiresAt: number }>();

async function fetchWithRetry(url: string, retries = 3): Promise<Response> {
  let lastError: Error | null = null;
  for (let i = 0; i < retries; i++) {
    try {
      const res = await fetch(url);
      if (res.ok) return res;
      // Don't retry 4xx (client errors)
      if (res.status >= 400 && res.status < 500) throw new Error(`HTTP ${res.status}`);
      lastError = new Error(`HTTP ${res.status}`);
    } catch (err) {
      lastError = err instanceof Error ? err : new Error(String(err));
    }
    if (i < retries - 1) await new Promise(r => setTimeout(r, 2 ** i * 1000));
  }
  throw lastError;
}

export async function fetchModels(folderId?: string): Promise<ModelInfo[]> {
  const cacheKey = folderId || '__default__';

  const cached = folderCache.get(cacheKey);
  if (cached && Date.now() < cached.expiresAt) {
    if (DEBUG) console.log(`Returning cached models for folder: ${cacheKey}`);
    return cached.models;
  }

  if (DEBUG) console.log(`Fetching models from backend API... ${folderId ? `for folder ${folderId}` : ''}`);

  const url = new URL(`${API_BASE_URL}/models`);
  if (folderId) url.searchParams.append('folderId', folderId);

  const response = await fetchWithRetry(url.toString());
  const data: ModelListResponse = await response.json();

  if (DEBUG) console.log(`Loaded ${data.count} models from backend`);
  if (DEBUG && data.debug) console.log('[API DEBUG]', data.debug);

  folderCache.set(cacheKey, { models: data.models, expiresAt: Date.now() + CACHE_TTL_MS });
  return data.models;
}

export async function checkHealth(): Promise<boolean> {
  try {
    const response = await fetch(`${API_BASE_URL}/models`);
    return response.ok;
  } catch {
    return false;
  }
}
